import React, { useEffect, useRef, useState } from "react";
import { NavLink, Link, Outlet, useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useSettings } from "../lib/settings";
import { useRealtimeEvent } from "../lib/realtime";
import { useTheme } from "../lib/theme";
import { toast } from "sonner";
import api from "../lib/api";
import ChatPopover from "./ChatPopover";
import ChatWindow from "./ChatWindow";
import SupportPopover from "./SupportPopover";
import NotificationsPanel from "./NotificationsPanel";
import FinancePanel from "./FinancePanel";
import TopBarFilters from "./TopBarFilters";
import BottomNav from "./BottomNav";
import EmeAi from "./EmeAi";
import { AssistantPanel } from "./assistant/AssistantPanel";
import EmailVerifyBanner from "./EmailVerifyBanner";
import { OfficeFilterProvider } from "../lib/officeFilter";
import Portal from "./Portal";
import {
  LayoutDashboard,
  Calendar,
  Users,
  FileText,
  MessageCircle,
  Building2,
  CreditCard,
  Inbox,
  ShoppingBag,
  LogOut,
  Stethoscope,
  Percent,
  UserCog,
  Wallet,
  Send,
  Sun,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  Moon,
  Database,
  Info, // eslint-disable-line no-unused-vars
  Bell,
  LifeBuoy,
  Settings,
  User,
  Globe,
  Clock,
  Home, // eslint-disable-line no-unused-vars
  ChevronDown,
  X,
} from "@/components/icons";

const ROLE_NAMES = {
  admin: "Administrador",
  doctor: "Doctor",
  secretary: "Secretaria",
  pharmacy: "Farmacia",
};

const PACIENTES_SUBITEMS = [
  { label: "Todos",             filter: "" },
  { label: "Activos",           filter: "activos" },
  { label: "Programados",       filter: "programados" },
  { label: "Último registrado", filter: "ultimo" },
  { label: "Alta",              filter: "alta" },
];

const MENUS = {
  admin: [
    { to: "/admin", icon: LayoutDashboard, label: "Resumen", end: true },
    { to: "/admin/usuarios", icon: UserCog, label: "Usuarios" },
    {
      to: "/admin/pacientes",
      icon: Users,
      label: "Pacientes",
    },
    { to: "/admin/referidos", icon: Send, label: "Referidos" },
    { to: "/admin/historial", icon: Clock, label: "Historial" },
    { to: "/admin/presencia", icon: User, label: "Presencia" },
    { to: "/admin/api-movil", icon: Globe, label: "API móvil" },
    { to: "/admin/comisiones", icon: Percent, label: "Comisiones" },
    { to: "/admin/pagos-farmacia", icon: Wallet, label: "Pagos Farmacia" },
  ],
  doctor: [
    { to: "/doctor", icon: LayoutDashboard, label: "Resumen", end: true, section: "Menú principal" },
    { to: "/doctor/agenda", icon: Calendar, label: "Agenda", section: "Menú principal" },
    {
      to: "/doctor/pacientes",
      icon: Users,
      label: "Pacientes",
      section: "Gestión",
    },
    { to: "/doctor/recetas", icon: FileText, label: "Recetas", section: "Gestión" },
    { to: "/doctor/referidos", icon: Send, label: "Referidos", section: "Gestión" },
    { to: "/doctor/finanzas", icon: Wallet, label: "Finanzas", section: "Operación" },
    { to: "/doctor/oficinas", icon: Building2, label: "Oficinas", section: "Operación" },
  ],
  secretary: [
    { to: "/secretaria", icon: LayoutDashboard, label: "Resumen", end: true },
    { to: "/secretaria/agenda", icon: Calendar, label: "Agenda" },
    {
      to: "/secretaria/pacientes",
      icon: Users,
      label: "Pacientes",
    },
    { to: "/secretaria/cobros", icon: CreditCard, label: "Cobros" },
  ],
  pharmacy: [
    { to: "/farmacia", icon: Inbox, label: "Recetas Recibidas", end: true },
    { to: "/farmacia/ventas", icon: ShoppingBag, label: "Ventas y Comisión" },
    { to: "/farmacia/historial", icon: Clock, label: "Historial semanal" },
  ],
};

const CHAT_PATH = {
  doctor: "/doctor/chat",
  secretary: "/secretaria/chat",
  admin: "/admin/chat",
};

export default function Layout() {
  const { user, logout } = useAuth();
  const { pharmacyEnabled, chatEnabled, supportEnabled, aiEnabled } = useSettings();
  const { theme: themeMode, toggle: toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(() => {
    try {
      return localStorage.getItem("sidebar_expanded") === "1";
    } catch {
      return false;
    }
  });
  const toggleExpanded = () => {
    setExpanded((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("sidebar_expanded", next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  };
  const [theme, setTheme] = useState("light");
  const [sidebarQuery, setSidebarQuery] = useState("");

  // Atajo global ⌘K / Ctrl+K → enfoca la barra de búsqueda inferior desde
  // cualquier página. Ignora combinaciones dentro de inputs / textareas para
  // no bloquear la escritura habitual.
  useEffect(() => {
    const onKey = (e) => {
      const isK = (e.key === "k" || e.key === "K");
      const mod = e.metaKey || e.ctrlKey;
      if (!isK || !mod) return;
      // Permitimos disparar incluso si el foco está en un input (útil para
      // "reenfocar el buscador global" desde otro input).
      e.preventDefault();
      const input = document.querySelector('[data-testid="topbar-patient-search"]');
      if (input) {
        input.focus();
        input.select();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Mini-menú del perfil (arriba-derecha) — se cierra con click fuera / Esc
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef(null);
  useEffect(() => {
    if (!profileMenuOpen) return;
    const onDown = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setProfileMenuOpen(false);
      }
    };
    const onKey = (e) => e.key === "Escape" && setProfileMenuOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [profileMenuOpen]);

  const [showInfo, setShowInfo] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatBounce, setChatBounce] = useState(false);
  const [pendingContactRequests, setPendingContactRequests] = useState(0);
  // Ventanas de chat MSN-style abiertas. Cada una: { id, name, photo_url, online, minimized }
  const [openChats, setOpenChats] = useState([]);
  // Contador de mensajes sin leer por ventana minimizada: { [contactId]: number }
  const [minimizedUnread, setMinimizedUnread] = useState({});
  // Contactos en línea (para pill del footer + notificaciones de conexión)
  // onlineContacts: array ordenado por conexión más reciente (desc), con { id, name, photo_url, connectedAt }
  const [onlineContacts, setOnlineContacts] = useState([]);
  const onlineContactsCount = onlineContacts.length;
  const prevOnlineRef = useRef(new Map()); // id -> { name, online, connectedAt }
  const onlineInitRef = useRef(false); // evita sonido/toast en el primer load
  const [supportOpen, setSupportOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [supportUnread, setSupportUnread] = useState(0);
  const [openTicketsCount, setOpenTicketsCount] = useState(0);
  const [referralsPendingCount, setReferralsPendingCount] = useState(0);
  const [appNotifs, setAppNotifs] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [tab, setTab] = useState("menu"); // menu | finanzas
  const lastSeenIdsRef = useRef(new Set());
  const initRef = useRef(false);

  // Global unread polling + new-message toast notification
  useEffect(() => {
    // Hide Emergent platform watermark
    const removeBadge = () => {
      document
        .querySelectorAll(
          '#emergent-badge, [id*="emergent-badge"], [class*="emergent-badge"], [class*="MadeWithEmergent"], a[href*="emergent.sh"], a[href*="emergent.com"], a[href*="emergentagent.com"]'
        )
        .forEach((el) => el.remove());
    };
    removeBadge();
    const badgeInterval = setInterval(removeBadge, 1000);
    window.addEventListener("beforeprint", removeBadge);

    if (!user) return () => clearInterval(badgeInterval);
    let mounted = true;
    const tick = async () => {
      try {
        const { data } = await api.get("/chat/unread");
        if (!mounted) return;
        const fromAdmin = data.by_sender.filter((s) => s.from_role === "admin");
        const fromNonAdmin = data.by_sender.filter((s) => s.from_role !== "admin");
        setSupportUnread(fromAdmin.reduce((acc, s) => acc + (s.count || 0), 0));
        setUnreadCount(fromNonAdmin.reduce((acc, s) => acc + (s.count || 0), 0));
        data.by_sender.forEach((s) => {
          const key = `${s.from_user_id}:${s.last_at}`;
          if (!lastSeenIdsRef.current.has(key)) {
            if (initRef.current) {
              const isAdminSender = s.from_role === "admin";
              toast.info(`${s.from_name}: ${s.last_message}`, {
                duration: 5000,
                action: {
                  label: "Abrir",
                  onClick: () => (isAdminSender ? setSupportOpen(true) : setChatOpen(true)),
                },
              });
            }
            lastSeenIdsRef.current.add(key);
          }
        });
        initRef.current = true;
      } catch {
        // ignore
      }
    };
    tick();
    const t = setInterval(tick, 15000); // fallback poll, SSE handles instant updates
    return () => {
      mounted = false;
      clearInterval(t);
      clearInterval(badgeInterval);
      window.removeEventListener("beforeprint", removeBadge);
    };
  }, [user?.id]);

  // Realtime: instant refresh when an event arrives
  const refreshUnread = async () => {
    if (!user) return;
    try {
      const [uRes, reqRes] = await Promise.all([
        api.get("/chat/unread"),
        api
          .get("/chat/contact-requests")
          .catch(() => ({ data: [] })),
      ]);
      const data = uRes.data;
      const fromAdmin = data.by_sender.filter((s) => s.from_role === "admin");
      const fromNonAdmin = data.by_sender.filter((s) => s.from_role !== "admin");
      setSupportUnread(fromAdmin.reduce((acc, s) => acc + (s.count || 0), 0));
      const chatUnread = fromNonAdmin.reduce((acc, s) => acc + (s.count || 0), 0);
      setPendingContactRequests(Array.isArray(reqRes.data) ? reqRes.data.length : 0);
      setUnreadCount(chatUnread);
    } catch {
      /* ignore */
    }
  };
  const refreshTickets = async () => {
    try {
      const { data } = await api.get("/support/tickets");
      setOpenTicketsCount(
        data.filter((t) => t.status === "open" || t.status === "in_progress").length
      );
    } catch {
      /* ignore */
    }
  };
  useRealtimeEvent("chat:new", (m) => {
    refreshUnread();
    // Si la ventana del remitente está minimizada, incrementar su contador
    const senderId = m.from_user_id;
    const isMinimized = openChats.some((c) => c.id === senderId && c.minimized);
    if (isMinimized) {
      setMinimizedUnread((cur) => ({ ...cur, [senderId]: (cur[senderId] || 0) + 1 }));
    }
    // Bounce footer button si el chat está cerrado
    if (!chatOpen) {
      setChatBounce(true);
      window.clearTimeout(window.__chatBounceT);
      window.__chatBounceT = window.setTimeout(() => setChatBounce(false), 2600);
    }
    toast.info(`${m.from_name}: ${m.content}`, {
      duration: 4500,
      action: { label: "Abrir", onClick: () => setChatOpen(true) },
    });
  });
  useRealtimeEvent("chat:contact_request", (r) => {
    refreshUnread();
    if (!chatOpen) {
      setChatBounce(true);
      window.clearTimeout(window.__chatBounceT);
      window.__chatBounceT = window.setTimeout(() => setChatBounce(false), 2600);
    }
    toast.info(`${r.from_name || "Alguien"} te envió una solicitud de contacto`, {
      duration: 6000,
      action: { label: "Ver", onClick: () => setChatOpen(true) },
    });
  });
  useRealtimeEvent("chat:contact_accepted", (r) => {
    refreshUnread();
    toast.success(`${r.by_name || "Tu contacto"} aceptó tu solicitud`, {
      duration: 4000,
      action: { label: "Abrir", onClick: () => setChatOpen(true) },
    });
  });
  useRealtimeEvent("support:new", () => {
    refreshTickets();
    toast.info("Nuevo ticket de soporte");
  });
  useRealtimeEvent("support:reply", () => refreshTickets());

  // Referrals pending count (only for doctors)
  const refreshReferralsPending = async () => {
    if (!user || user.role !== "doctor") return;
    try {
      const { data } = await api.get("/referrals?box=received&status=pendiente");
      setReferralsPendingCount(Array.isArray(data) ? data.length : 0);
    } catch {
      /* ignore */
    }
  };
  useRealtimeEvent("referral:received", () => {
    refreshReferralsPending();
    refreshAppNotifs();
  });

  // Reproduce un "ding" suave con Web Audio API (sin requerir assets)
  const playConnectSound = () => {
    // Respeta la preferencia del usuario (cd:prefs:chat_sound)
    try {
      const raw = window.localStorage.getItem("cd:prefs:chat_sound");
      if (raw != null && JSON.parse(raw) === false) return;
    } catch {
      /* fallthrough: default on */
    }
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const now = ctx.currentTime;
      // Dos tonos tipo iMessage/MSN muy breves
      const notes = [
        { f: 880, t: 0.0, d: 0.12 },
        { f: 1318, t: 0.09, d: 0.18 },
      ];
      notes.forEach(({ f, t, d }) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(f, now + t);
        gain.gain.setValueAtTime(0, now + t);
        gain.gain.linearRampToValueAtTime(0.12, now + t + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + t + d);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now + t);
        osc.stop(now + t + d + 0.02);
      });
      setTimeout(() => ctx.close().catch(() => {}), 500);
    } catch {
      /* ignore */
    }
  };

  // Contactos online: polling y detección de conexión (toast cuando alguien se conecta)
  const showPresenceToast = (c, type) => {
    // type: "connected" | "disconnected"
    const isConnected = type === "connected";
    const specialty =
      c.specialty ||
      (c.role === "doctor"
        ? "Doctor"
        : c.role === "secretary"
          ? "Secretaria"
          : c.role === "admin"
            ? "Admin"
            : "");
    const bgIcon = isConnected ? "bg-[#22C55E]" : "bg-[#EF4444]";
    const ringIcon = "ring-white/30";
    const message = isConnected
      ? "acaba de iniciar sesión"
      : "acaba de cerrar sesión";
    const testidPrefix = isConnected ? "login-toast" : "logout-toast";

    toast.custom(
      (id) => (
        <div
          data-testid={`${testidPrefix}-${c.id}`}
          onClick={() => {
            if (isConnected) openChatWith(c);
            toast.dismiss(id);
          }}
          className={`cai-toast pointer-events-auto w-[360px] rounded-[22px] p-3 flex items-center gap-3 transition-transform ${
            isConnected ? "cursor-pointer hover:-translate-y-0.5" : "cursor-default"
          }`}
        >
          <span
            className={`w-11 h-11 rounded-full flex-shrink-0 flex items-center justify-center text-white ring-4 ${bgIcon} ${ringIcon}`}
            aria-hidden="true"
          >
            <User className="w-5 h-5" strokeWidth={2} weight="regular" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[13.5px] text-white leading-snug truncate">
              <span className="font-semibold">{c.name}</span>
              {specialty && (
                <span className="text-white/80 font-medium"> · {specialty}</span>
              )}
            </div>
            <div className="text-[12.5px] text-white/85 mt-0.5">
              {message}
            </div>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              toast.dismiss(id);
            }}
            data-testid={`${testidPrefix}-close-${c.id}`}
            className="w-7 h-7 rounded-full flex items-center justify-center text-white/80 hover:bg-white/20 hover:text-white transition-colors flex-shrink-0"
            aria-label="Cerrar"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="w-3.5 h-3.5"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      ),
      { duration: 5000 }
    );
  };

  const refreshOnlineContacts = async () => {
    if (!user) return;
    const isStaff = ["doctor", "secretary", "admin"].includes(user.role);
    if (!isStaff) return;
    try {
      const { data } = await api.get("/chat/contacts");
      const list = Array.isArray(data) ? data : [];
      // Detectar transiciones offline→online y online→offline
      const prev = prevOnlineRef.current;
      const next = new Map();
      const now = Date.now();
      let newConnections = 0;
      for (const c of list) {
        const wasOnline = prev.get(c.id)?.online;
        const prevConnectedAt = prev.get(c.id)?.connectedAt;
        const connectedAt = c.online
          ? wasOnline
            ? prevConnectedAt || now
            : now
          : null;
        next.set(c.id, { name: c.name, online: c.online, connectedAt });
        if (c.online && wasOnline === false && onlineInitRef.current) {
          newConnections++;
          showPresenceToast(c, "connected");
        } else if (!c.online && wasOnline === true && onlineInitRef.current) {
          showPresenceToast(c, "disconnected");
        }
      }
      if (newConnections > 0) playConnectSound();
      prevOnlineRef.current = next;
      // Construye lista de online ordenada por conexión más reciente (desc)
      const onlineSorted = list
        .filter((c) => c.online)
        .map((c) => ({
          id: c.id,
          name: c.name,
          photo_url: c.photo_url,
          connectedAt: next.get(c.id)?.connectedAt || 0,
        }))
        .sort((a, b) => (b.connectedAt || 0) - (a.connectedAt || 0));
      setOnlineContacts(onlineSorted);
      onlineInitRef.current = true;
    } catch {
      /* ignore */
    }
  };
  useEffect(() => {
    refreshOnlineContacts();
    if (!user) return;
    const t = setInterval(refreshOnlineContacts, 25000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // App notifications (appointment reschedules / cancels / etc.)
  const refreshAppNotifs = async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/notifications");
      setAppNotifs(data || []);
    } catch {
      /* ignore */
    }
  };
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/notifications");
        if (!cancelled) setAppNotifs(data || []);
      } catch {
        /* ignore */
      }
      // Refresh referrals pending count for doctors
      if (user.role === "doctor") {
        try {
          const { data } = await api.get(
            "/referrals?box=received&status=pendiente"
          );
          if (!cancelled)
            setReferralsPendingCount(Array.isArray(data) ? data.length : 0);
        } catch {
          /* ignore */
        }
      }
    };
    load();
    const id = setInterval(load, 30000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [user?.id]);
  useRealtimeEvent("notification:new", (n) => {
    refreshAppNotifs();
    toast.info(n.title || "Nueva notificación", {
      description: n.message,
      duration: 6000,
      action: { label: "Ver", onClick: () => setNotifOpen(true) },
    });
  });

  // Helper compartido para abrir una ventana de chat con un contacto
  const openChatWith = (c) => {
    if (!c || !c.id) return;
    setOpenChats((cur) => {
      const idx = cur.findIndex((x) => x.id === c.id);
      if (idx >= 0) {
        const copy = cur.slice();
        copy[idx] = { ...copy[idx], minimized: false };
        return copy;
      }
      const next = [
        ...cur,
        {
          id: c.id,
          name: c.name,
          photo_url: c.photo_url,
          online: c.online !== false,
          role: c.role,
          specialty: c.specialty,
          minimized: false,
        },
      ];
      return next.length > 3 ? next.slice(-3) : next;
    });
    setChatBounce(false);
  };

  if (!user) return null;

  // Forzar a los doctores ya registrados (antes de que existiera el campo)
  // a seleccionar su especialidad antes de usar el sistema.
  if (user.role === "doctor" && !user.specialty) {
    return <Navigate to="/elegir-especialidad" replace />;
  }

  const baseItems = MENUS[user.role] || [];
  // Hide pharmacy-related nav for non-admin roles when their effective
  // `pharmacy_enabled` flag is off. Admin always sees all admin pages so
  // they can manage per-doctor pharmacy access even with the module globally
  // hidden from public-facing pages (login/register/onboarding).
  const PHARMACY_PATHS = new Set([
    "/admin/comisiones",
    "/admin/pagos-farmacia",
  ]);
  const items =
    user.role === "admin" || pharmacyEnabled
      ? baseItems
      : baseItems.filter((it) => !PHARMACY_PATHS.has(it.to));

  // Resolve the current section title from the active nav item so we can
  // render a dynamic heading above the menu ("Resumen", "Pacientes", ...).
  // Note: computed as plain value (not useMemo) so it stays below the
  // early returns without breaking the Rules of Hooks.
  const _path = location.pathname;
  let _best = null;
  let _exact = null;
  for (const it of items) {
    if (it.to === _path) { _exact = it; break; }
    if (_path.startsWith(it.to) && (!_best || it.to.length > _best.to.length)) {
      _best = it;
    }
  }
  const activeItem = _exact || _best;
  const sectionTitle = activeItem?.label || ROLE_NAMES[user.role] || "Menú";

  const _exportDb = async () => {
    try {
      const path = user.role === "admin" ? "/admin/export" : "/doctor/export";
      const { data } = await api.get(path);
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `eme-health-${user.role}-${new Date()
        .toISOString()
        .slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Base de datos descargada");
    } catch {
      toast.error("Solo admin y doctor pueden exportar la base de datos");
    }
  };

  return (
    <OfficeFilterProvider>
    <div className="h-full bg-[#FAFAFC] flex flex-col overflow-hidden">
    <div className="flex-1 flex min-h-0 overflow-hidden">
      {/* ═══ SIDEBAR IZQUIERDO (full nav, estilo Shakuro) ══════════════ */}
      <aside
        data-testid="app-sidebar"
        className="hidden md:flex flex-col w-[240px] flex-shrink-0 bg-[#FAFAFC] px-3 py-5 min-h-0"
      >
        {/* Brand: logo + nombre */}
        <button
          type="button"
          onClick={() => navigate(`/${user.role === "secretary" ? "secretaria" : user.role === "pharmacy" ? "farmacia" : user.role}`)}
          className="flex items-center gap-2.5 px-2 py-1.5 mb-5 hover:opacity-100 transition group"
          data-testid="sidebar-logo"
        >
          <img
            src="/brand/consudoctor-icon.png"
            alt="ConsuDoctor"
            className="w-8 h-8 object-contain"
          />
          <span className="text-[15px] font-bold text-black tracking-tight">ConsuDoctor</span>
        </button>

        {/* Navegación agrupada por secciones */}
        <nav className="flex flex-col gap-0.5 flex-1 min-h-0 overflow-y-auto nice-scroll" data-testid="sidebar-nav">
          {(() => {
            const grouped = [];
            let currentSection = null;
            for (const it of items) {
              if (it.section !== currentSection) {
                currentSection = it.section;
                if (currentSection) {
                  grouped.push({ type: "section", label: currentSection, key: `s-${currentSection}` });
                }
              }
              grouped.push({ type: "item", data: it, key: it.to });
            }
            return grouped.map((row) => {
              if (row.type === "section") {
                return (
                  <div
                    key={row.key}
                    className="px-3 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-wider text-black/35"
                  >
                    {row.label}
                  </div>
                );
              }
              const it = row.data;
              const active =
                location.pathname === it.to ||
                (it.to !== `/${user.role}` &&
                  it.to !== "/doctor" &&
                  location.pathname.startsWith(it.to));
              const Icon = it.icon || LayoutDashboard;
              // Badge dinámico por ruta
              let badge = null;
              if (it.to.endsWith("/referidos") && referralsPendingCount > 0) {
                badge = referralsPendingCount > 9 ? "9+" : referralsPendingCount;
              }
              return (
                <NavLink
                  key={row.key}
                  to={it.to}
                  end={it.end}
                  data-testid={`nav-${it.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={
                    "flex items-center gap-2.5 px-3 py-2 rounded-xl text-[13.5px] transition " +
                    (active
                      ? "bg-white border border-black/[0.06] shadow-[0_1px_2px_rgba(0,0,0,0.04),0_4px_12px_-6px_rgba(0,0,0,0.08)] text-[#0071E3] font-semibold"
                      : "text-black/45 hover:bg-black/[0.04] hover:text-black/80 font-normal")
                  }
                >
                  <Icon
                    className={`w-[18px] h-[18px] flex-shrink-0 ${active ? "text-[#0071E3]" : "text-black/40"}`}
                    strokeWidth={active ? 2 : 1.5}
                    weight="regular"
                  />
                  <span className="truncate flex-1">{it.label}</span>
                  {badge && (
                    <span
                      data-testid={`nav-badge-${it.label.toLowerCase().replace(/\s/g, "-")}`}
                      className={`min-w-[18px] h-[18px] px-1.5 rounded-full text-[10px] font-bold inline-flex items-center justify-center flex-shrink-0 ${
                        active ? "bg-[#0071E3] text-white" : "bg-red-500 text-white"
                      }`}
                    >
                      {badge}
                    </span>
                  )}
                </NavLink>
              );
            });
          })()}

          {/* Separador antes de acciones secundarias */}
        </nav>

        {/* Espaciador */}
        <div className="flex-1" />

        {/* Tarjeta de usuario abajo (clickeable → perfil, con logout a la derecha) */}
        <div className="mt-3 flex items-center gap-2 px-2 py-2 rounded-xl hover:bg-black/[0.04] transition group flex-shrink-0">
          <button
            type="button"
            onClick={() => navigate("/perfil")}
            data-testid="sidebar-user-card"
            className="flex items-center gap-2.5 flex-1 min-w-0 text-left"
          >
            <span className="w-9 h-9 rounded-full flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#D7D4FF] to-[#F7D6EC] flex-shrink-0">
              {user.photo_url ? (
                <img src={user.photo_url} alt={user.name} className="w-full h-full object-cover" />
              ) : (
                <span className="text-[11px] font-bold text-white">
                  {(user.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}
                </span>
              )}
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-[13px] font-semibold text-black truncate">
                {user.name || "Doctor"}
              </span>
              <span className="block text-[11px] text-black/50 truncate">
                {user.email || ""}
              </span>
            </span>
          </button>
          <button
            type="button"
            onClick={() => { logout(); navigate("/login"); }}
            data-testid="sidebar-logout"
            title="Cerrar sesión"
            className="w-8 h-8 rounded-lg flex items-center justify-center text-black/50 hover:bg-red-50 hover:text-red-500 transition flex-shrink-0"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.75} weight="regular" />
          </button>
        </div>
      </aside>

      {/* ═══ ÁREA PRINCIPAL ═══════════════════════════════════════════ */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        {/* Topbar mínimo: solo acciones a la derecha + burger móvil */}
        <nav
          data-testid="top-nav"
          className="sticky top-0 z-40 bg-[#FAFAFC]/85 backdrop-blur-md"
        >
          <div className="w-full flex items-center justify-between gap-4 px-6 py-3">
            {/* Mobile: brand + burger */}
            <div className="flex items-center gap-2 md:hidden">
              <img
                src="/brand/consudoctor-icon.png"
                alt="ConsuDoctor"
                className="w-8 h-8 object-contain"
              />
            </div>
            <div className="hidden md:block" />

            {/* Acciones derecha */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setOpen(!open)}
                className="md:hidden w-10 h-10 rounded-xl bg-white border border-black/10 flex items-center justify-center"
                title="Menú"
                data-testid="mobile-menu-toggle"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 text-black">
                  <line x1="4" y1="6" x2="20" y2="6" />
                  <line x1="4" y1="12" x2="20" y2="12" />
                  <line x1="4" y1="18" x2="20" y2="18" />
                </svg>
              </button>

              <div className="flex items-center gap-0.5 bg-white border border-black/[0.04] rounded-lg p-1">
                <button
                  type="button"
                  onClick={() => setSupportOpen(true)}
                  data-testid="topbar-support-btn"
                  title="Soporte"
                  className="relative w-8 h-8 rounded-md flex items-center justify-center text-black/70 hover:bg-black/[0.05] transition"
                >
                  <LifeBuoy className="w-4 h-4" strokeWidth={1.75} weight="regular" />
                </button>
                <button
                  type="button"
                  onClick={toggleTheme}
                  data-testid="topbar-theme-toggle"
                  title={themeMode === "dark" ? "Modo claro" : "Modo oscuro"}
                  className="relative w-8 h-8 rounded-md flex items-center justify-center text-black/70 hover:bg-black/[0.05] transition"
                >
                  {themeMode === "dark" ? (
                    <Sun className="w-4 h-4" strokeWidth={1.75} weight="regular" />
                  ) : (
                    <Moon className="w-4 h-4" strokeWidth={1.75} weight="regular" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setNotifOpen((v) => !v)}
                  data-testid="notifications-button"
                  title="Notificaciones"
                  className="relative w-8 h-8 rounded-md flex items-center justify-center text-black/70 hover:bg-black/[0.05] transition"
                >
                  <Bell className="w-4 h-4" strokeWidth={1.75} weight="regular" />
                  {(appNotifs.some((n) => !n.read_at) || unreadCount > 0) && (
                    <span data-testid="notifications-dot" className="absolute top-1 right-1 w-2 h-2 rounded-full bg-[#FF6B6B] ring-2 ring-white" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Sub-nav row: submenu pills (title omitido — cada página tiene su H1) */}
          {activeItem?.subItems && (
            <div className="w-full px-6 pb-2 flex items-center justify-end gap-4">
              <div
                className="flex items-center gap-1 flex-wrap"
                data-testid={`submenu-${activeItem.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {activeItem.subItems.map((sub) => {
                  const [subPath, subQuery] = sub.to.split("?");
                  const currentQuery = location.search.replace(/^\?/, "");
                  const isSubActive =
                    location.pathname === subPath &&
                    (subQuery || "") === currentQuery;
                  return (
                    <button
                      key={sub.to}
                      type="button"
                      onClick={() => navigate(sub.to)}
                      data-testid={`subnav-${sub.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`h-8 rounded-xl px-3 text-[12px] font-medium transition ${
                        isSubActive
                          ? "text-[#0071E3] font-semibold"
                          : "text-black/55 hover:text-black"
                      }`}
                    >
                      {sub.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </nav>

      {/* Mobile drawer — se abre desde el botón de menú móvil */}
      {open && (
        <div
          className="md:hidden fixed inset-0 z-50 bg-black/40"
          onClick={() => setOpen(false)}
        >
          <div
            className="absolute top-0 left-0 h-full w-72 bg-[#FAFAFC] p-6 flex flex-col gap-3 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col items-start mb-4">
              <span className="text-[15px] font-semibold text-black">{user.name || user.full_name}</span>
            </div>
            {items.map((it) => {
              const active = location.pathname === it.to || (it.to !== `/${user.role}` && it.to !== "/doctor" && location.pathname.startsWith(it.to));
              return (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.end}
                  onClick={() => setOpen(false)}
                  className={
                    active
                      ? "text-[15px] font-semibold text-[#0071E3] py-1.5 bg-transparent"
                      : "text-[15px] font-normal text-black hover:text-[#0071E3] py-1.5 bg-transparent"
                  }
                >
                  {it.label}
                </NavLink>
              );
            })}
          </div>
        </div>
      )}

      {/* Main + panel IA */}
      <div className="flex-1 flex min-h-0">
      <main className="flex-1 flex flex-col min-w-0 min-h-0 overflow-y-auto nice-scroll">
        <div className="w-full px-6 pb-16 pt-1 fade-up">
          <EmailVerifyBanner />
          <Outlet />
        </div>
      </main>
      {user.role === "doctor" && (
        <AssistantPanel chatUnread={unreadCount} appNotifs={appNotifs} onRefreshNotifs={refreshAppNotifs} />
      )}
      </div>
      </div>
      {/* /Área principal */}
      </div>
      {/* /flex sidebar+main */}

      {/* ── Chat floating pill + minimized windows (bottom-right fixed) ──
       *   Reemplaza el footer antiguo con una barra inferior completa.
       *   Solo flota el chat; sin copyright, sin fondo gris de página completa.
       */}
      <div
        data-testid="chat-dock"
        className={`fixed z-30 right-0 bottom-0 flex items-end gap-2 pointer-events-none ${user.role === "doctor" ? "xl:hidden" : ""}`}
      >
        <div className="flex items-end gap-2 pointer-events-auto pr-0 pb-0">
          {/* Pills de ventanas de chat minimizadas */}
          {openChats
            .filter((c) => c.minimized)
            .map((c) => {
              const unread = minimizedUnread[c.id] || 0;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => {
                    setOpenChats((cur) =>
                      cur.map((x) =>
                        x.id === c.id ? { ...x, minimized: false } : x
                      )
                    );
                    setMinimizedUnread((cur) => {
                      const n = { ...cur };
                      delete n[c.id];
                      return n;
                    });
                  }}
                  data-testid={`chat-restore-${c.id}`}
                  title={c.name}
                  className={`relative h-11 w-[340px] pl-2 pr-3 bg-white border border-b-0 rounded-none flex items-center gap-2 hover:bg-black/[0.03] transition shadow-[0_-4px_14px_rgba(0,0,0,0.08)] ${
                    unread > 0
                      ? "border-[#0071E3]/40 chat-bounce"
                      : "border-black/[0.08]"
                  }`}
                >
                  <span className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 relative bg-gradient-to-br from-[#D7D4FF] to-[#F7D6EC] flex items-center justify-center">
                    {c.photo_url ? (
                      <img
                        src={c.photo_url}
                        alt={c.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-white font-bold text-[10.5px]">
                        {(c.name || "?")
                          .split(" ")
                          .map((s) => s[0])
                          .slice(0, 2)
                          .join("")}
                      </span>
                    )}
                    {c.online && (
                      <span className="absolute right-0 bottom-0 w-2 h-2 rounded-full bg-[#22C55E] ring-2 ring-white" />
                    )}
                  </span>
                  <span className="flex-1 min-w-0 text-left">
                    <span className="block text-[13px] font-semibold text-black truncate">
                      {c.name}
                    </span>
                  </span>
                  {unread > 0 && (
                    <span
                      data-testid={`chat-mini-badge-${c.id}`}
                      className="min-w-[18px] h-[18px] px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0"
                    >
                      {unread > 9 ? "9+" : unread}
                    </span>
                  )}
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenChats((cur) => cur.filter((x) => x.id !== c.id));
                      setMinimizedUnread((cur) => {
                        const n = { ...cur };
                        delete n[c.id];
                        return n;
                      });
                    }}
                    className="w-6 h-6 rounded-lg flex items-center justify-center text-black/40 hover:bg-black/[0.06] hover:text-black transition text-[15px] leading-none flex-shrink-0"
                  >
                    ×
                  </span>
                </button>
              );
            })}

          {/* Chat pill principal (conectados) — pegado al borde inferior,
           *   mismo ancho que el popover (380px) y cuadrado. */}
          {CHAT_PATH[user.role] && chatEnabled && (
            <div
              data-testid="footer-chat-pill"
              className={`relative h-11 w-[380px] px-2 rounded-none border border-b-0 border-r-0 flex items-center gap-1 transition shadow-[0_-4px_14px_rgba(0,0,0,0.08)] ${
                chatOpen
                  ? "bg-[#2C2C2E] border-[#2C2C2E] text-white"
                  : "bg-white border-black/[0.08] text-black/70"
              } ${chatBounce ? "chat-bounce" : ""}`}
            >
              <button
                type="button"
                onClick={() => { setChatOpen((v) => !v); setChatBounce(false); }}
                data-testid="footer-chat-button"
                title="Conectados"
                className={`relative h-9 flex-1 px-4 rounded-none flex items-center gap-2.5 transition ${
                  chatOpen ? "hover:bg-white/10" : "hover:bg-black/[0.03]"
                }`}
              >
                <span
                  className={`w-2 h-2 rounded-full ${
                    onlineContactsCount > 0
                      ? chatOpen
                        ? "bg-[#4ADE80]"
                        : "bg-[#22C55E]"
                      : chatOpen
                        ? "bg-white/30"
                        : "bg-black/25"
                  }`}
                  aria-hidden="true"
                />
                <span
                  className="text-[13px] font-semibold whitespace-nowrap"
                  data-testid="footer-chat-online-count"
                >
                  {onlineContactsCount}{" "}
                  {onlineContactsCount === 1 ? "conectado" : "conectados"}
                </span>
              </button>
              {(unreadCount + pendingContactRequests) > 0 && (
                <span
                  data-testid="footer-chat-badge"
                  className="min-w-[20px] h-[20px] mr-1 px-1.5 rounded-full bg-red-500 text-white text-[10.5px] font-bold flex items-center justify-center"
                >
                  {unreadCount + pendingContactRequests}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Barra inferior fija removida — el buscador ahora vive en el topbar
       * SOLO para el doctor (la secretaria ya no tiene búsqueda global). */}

      <ChatPopover
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        onOpenChat={openChatWith}
      />
      {/* Ventanas de chat flotantes (no minimizadas) */}
      {openChats
        .filter((c) => !c.minimized)
        .map((c, i) => (
          <ChatWindow
            key={c.id}
            contact={c}
            onClose={(id) => setOpenChats((cur) => cur.filter((x) => x.id !== id))}
            onMinimize={(id) =>
              setOpenChats((cur) =>
                cur.map((x) => (x.id === id ? { ...x, minimized: true } : x))
              )
            }
            style={{ right: `${380 + i * 348}px` }}
          />
        ))}
      <SupportPopover open={supportOpen} onClose={() => setSupportOpen(false)} />
      <NotificationsPanel
        open={notifOpen}
        onClose={() => setNotifOpen(false)}
        unreadChat={unreadCount}
        appNotifs={appNotifs}
        onOpenChat={() => setChatOpen(true)}
        onOpenSupport={() => setSupportOpen(true)}
        onRefreshNotifs={refreshAppNotifs}
      />

      {showInfo && (
        <Portal>
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-md p-4">
            <div className="bg-white rounded-xl p-6 w-full max-w-md text-center shadow-2xl">
              <div className="w-16 h-16 rounded-xl bg-white mx-auto flex items-center justify-center mb-3 border border-[#E5E7EB]">
                <img src="/brand/consudoctor-icon.png" alt="ConsuDoctor" className="w-12 h-12 object-contain" />
              </div>
              <h3 className="text-xl font-extrabold text-black">
                ConsuDoctor
              </h3>
              <p className="text-sm text-black/70 mt-1">
                Sistema EMR completo para clínicas. Versión 1.0
              </p>
              <button
                onClick={() => setShowInfo(false)}
                className="mt-5 btn-primary"
              >
                Cerrar
              </button>
            </div>
          </div>
        </Portal>
      )}
      {aiEnabled && user.role !== "doctor" && <EmeAi />}
    </div>
    </OfficeFilterProvider>
  );
}

function CircleIconBtn({ onClick, children, testid, title, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      title={title}
      className={`w-10 h-10 rounded-xl flex items-center justify-center transition ${
        disabled
          ? "bg-white/30 text-[#1B405B]/30 cursor-not-allowed"
          : "bg-white/55 text-[#163144] hover:bg-white border border-white/60"
      }`}
    >
      {children}
    </button>
  );
}

function ThemeToggle({ expanded = false }) {
  const { theme, toggle } = useTheme();
  const dark = theme === "dark";
  const label = dark ? "Modo claro" : "Modo oscuro";
  return (
    <button
      onClick={toggle}
      title={expanded ? undefined : label}
      aria-label={label}
      data-testid="theme-toggle"
      className={
        expanded
          ? "nav-halo h-11 rounded-xl flex items-center gap-3 pl-3 pr-4 transition-colors"
          : "nav-halo w-12 h-12 rounded-xl flex items-center justify-center transition-colors"
      }
    >
      {dark ? (
        <Sun className="w-[20px] h-[20px] text-amber-300 shrink-0" strokeWidth={1.8} />
      ) : (
        <Moon className="w-[20px] h-[20px] shrink-0" strokeWidth={1.8} />
      )}
      {expanded && (
        <span className="text-sm font-bold truncate">{label}</span>
      )}
    </button>
  );
}

